import apiClient from 'services/_infra/apiClient';
import type {
  FemaAlertPayload,
  FemaBackendResultClass,
  FemaBackendSendResult,
  FemaEnrichedAdvisory,
  FemaEnvironment,
  FemaOutcomeFieldFinding,
  FemaSendGateResponse,
  FemaSubmissionOutcome,
  FemaSubmissionOutcomeClass,
  FemaSubmitResponse,
} from 'types/fema.types';
import { VALID_FEMA_ENVIRONMENTS } from 'types/fema.types';
import type { FemaCancelResponse } from 'types/femaMessage.types';
import {
  FEMA_MESSAGE_VIEWER_URL,
  getFemaStatusInfo,
} from '../../features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/femaStatusCodes';

export const VALID_ENVIRONMENTS = VALID_FEMA_ENVIRONMENTS;

/** Imperative lifecycle commands; references are owned by the backend. */
export const updateFemaMessage = async (messageId: number, payload: FemaAlertPayload) => {
  const response = await apiClient.post(`/v2/fema/${messageId}/update`, payload);
  return response.data as {
    message: string;
    result: { messageId: number; referencesMessageId: number; accepted: boolean; codes: string[] };
  };
};

/**
 * ONE CAP Cancel for the ONE message named — earlier and later messages in its history stay on
 * the air (card BE-28). The body is `{ reason }` and nothing else; a body carrying `scope` is
 * refused with a 400. The test-matrix runner is the only caller, and it cancels the head its run
 * created, so the default reason is its own.
 */
export const cancelFemaMessage = async (
  messageId: number,
  reason: string = 'Completing the automated TDL test matrix lifecycle.',
) => {
  const response = await apiClient.post(`/v2/fema/${messageId}/cancel`, { reason });
  return response.data as FemaCancelResponse;
};
export type { FemaAlertPayload, FemaEnvironment, FemaSubmissionOutcome } from 'types/fema.types';

export type FemaSubmissionProcessedResult = {
  valid: boolean;
  acceptanceMessage: string;
  message: string;
  findings?: FemaOutcomeFieldFinding[];
};

/**
 * Validate the minimum payload shape before the submit call — only what the backend model
 * requires and the form can get wrong (status/msgType/scope, the first info block's
 * headline/description/eventCode/expires). `identifier`/`sender`/`sent`/`source` are backend-owned.
 * @param payload Candidate payload from UI state.
 * @returns True when the required fields are present and non-empty.
 */
export const validateFemaPreflightPayload = (payload: unknown): payload is FemaAlertPayload => {
  if (!payload || typeof payload !== 'object') return false;

  const value = payload as Record<string, unknown>;

  if (typeof value.status !== 'string' || !value.status.trim()) return false;
  if (typeof value.msgType !== 'string' || !value.msgType.trim()) return false;
  if (typeof value.scope !== 'string' || !value.scope.trim()) return false;

  if (!Array.isArray(value.info) || value.info.length === 0) return false;

  const info = value.info[0];
  if (!info || typeof info !== 'object') return false;

  const infoValue = info as Record<string, unknown>;
  if (typeof infoValue.headline !== 'string' || !infoValue.headline.trim()) return false;
  if (typeof infoValue.description !== 'string' || !infoValue.description.trim()) return false;
  if (
    (typeof infoValue.expires !== 'string' && !(infoValue.expires instanceof Date)) ||
    (typeof infoValue.expires === 'string' && !infoValue.expires.trim())
  )
    return false;
  if (!Array.isArray(infoValue.eventCode) || infoValue.eventCode.length === 0) return false;

  return true;
};

/** The backend's answer while the send gate is closed: built and signed, NOT sent. */
export const isSendGateResponse = (body: unknown): body is FemaSendGateResponse =>
  !!body &&
  typeof body === 'object' &&
  (body as { code?: unknown }).code === 'REACHED_SEND_POINT' &&
  typeof (body as { signedXml?: unknown }).signedXml === 'string';

const isBackendSendResult = (body: unknown): body is FemaBackendSendResult =>
  !!body &&
  typeof body === 'object' &&
  typeof (body as { resultClass?: unknown }).resultClass === 'string';

const isFrontendOutcome = (body: unknown): body is FemaSubmissionOutcome =>
  !!body && typeof body === 'object' && typeof (body as { outcome?: unknown }).outcome === 'string';

/** Enriches backend advisories, collapsing repeated successful mobile-carrier code-10 acks. */
export const enrichFemaAdvisories = (
  advisories: { code: string; message: string }[] | undefined,
): FemaEnrichedAdvisory[] => {
  let hasCode10Acknowledgement = false;

  return (advisories ?? []).reduce<FemaEnrichedAdvisory[]>((enrichedAdvisories, advisory) => {
    if (advisory.code === '10') {
      if (hasCode10Acknowledgement) return enrichedAdvisories;
      hasCode10Acknowledgement = true;
    }

    enrichedAdvisories.push({
      ...getFemaStatusInfo(advisory.code),
      backendMessage: advisory.message,
    });
    return enrichedAdvisories;
  }, []);
};

/**
 * Picks the advisory the result panel should lead with: an error outrun everything else, then a
 * code whose own channel says the alert was NOT disseminated (e.g. 301), otherwise the first one.
 */
export const pickPrimaryFemaAdvisory = (
  advisories: FemaEnrichedAdvisory[],
): FemaEnrichedAdvisory | undefined =>
  advisories.find((advisory) => advisory.isError) ??
  advisories.find((advisory) => advisory.disseminated === false) ??
  advisories[0];

/** IPAWS Profile 301: `<status>` was not `Actual` — by far the most common not-disseminated cause. */
const NON_IPAWS_TEST_ALERT_CODE = '301';

/** A plain-language reason for the operator, kept simpler than the registry's field-level remark. */
const getNotDisseminatedReason = (advisory: FemaEnrichedAdvisory): string =>
  advisory.code === NON_IPAWS_TEST_ALERT_CODE
    ? 'This is a test alert, not a real alert, so it was not published to the public.'
    : advisory.backendMessage || advisory.remark;

/**
 * The backend classifies into SEVEN result classes (FEMA requirements §4.8); the result panel
 * shows six. Transport and authentication success are steps on the way, not acceptance — the
 * requirements say twice that HTTP 200 is not IPAWS acceptance — so both render as "not confirmed".
 */
export const mapBackendResultClass = (
  resultClass: FemaBackendResultClass,
): FemaSubmissionOutcomeClass => {
  switch (resultClass) {
    case 'IPAWS_ACCEPTANCE':
      return 'acceptance';
    case 'IPAWS_ADVISORY':
      return 'advisory';
    case 'IPAWS_WARNING':
      return 'warning';
    case 'IPAWS_REJECTION':
      return 'rejection';
    case 'NETWORK_FAILURE':
      return 'network-failure';
    case 'TRANSPORT_SUCCESS':
    case 'AUTHENTICATION_SUCCESS':
    default:
      return 'transport-unknown';
  }
};

const toFemaEnvironment = (value: unknown): FemaEnvironment | null => {
  if (typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return VALID_FEMA_ENVIRONMENTS.find((environment) => environment === upper) ?? null;
};

/**
 * Multi-COG contract §1: the class that the backend answered under, read from
 * `body.tenant.environment` (`tdl` | `demo` | `production`, any case). Never a front-end constant.
 * @returns The upper-case class, or `null` when the answer names no known class.
 */
export const readAnsweredFemaEnvironment = (body: unknown): FemaEnvironment | null => {
  if (!body || typeof body !== 'object' || !('tenant' in body)) return null;
  const { tenant } = body;
  if (!tenant || typeof tenant !== 'object' || !('environment' in tenant)) return null;
  return toFemaEnvironment(tenant.environment);
};

/**
 * Normalize whatever a 2xx from `POST /v2/fema` carries into the outcome the result panel
 * renders. The send-gate response is NOT an outcome — callers check `isSendGateResponse` first.
 */
export const normalizeSubmitResponse = (
  body: FemaSubmitResponse | unknown,
): FemaSubmissionOutcome => {
  const receivedAt = new Date().toISOString();
  const base = {
    environment: readAnsweredFemaEnvironment(body),
    destination: 'IPAWS-OPEN' as const,
    receivedAt,
  };

  if (isFrontendOutcome(body)) return body;

  if (isBackendSendResult(body)) {
    const outcome = mapBackendResultClass(body.resultClass);
    const capIdentifier = body.capIdentifier ?? '';
    const advisories = enrichFemaAdvisories(body.advisories);
    const primaryAdvisory = pickPrimaryFemaAdvisory(advisories);
    const notDisseminated = advisories.find((advisory) => advisory.disseminated === false);
    const cogId = body.tenant?.cogId;
    const verificationUrl = cogId ? FEMA_MESSAGE_VIEWER_URL : undefined;

    switch (outcome) {
      case 'acceptance':
        return {
          ...base,
          outcome,
          capIdentifier,
          acceptedAt: body.sentAt ?? receivedAt,
          cogId,
          verificationUrl,
        };
      case 'advisory':
        // A channel's own status says the alert did NOT go out — that overrides a plain advisory.
        if (notDisseminated) {
          return {
            ...base,
            outcome: 'accepted-not-disseminated',
            capIdentifier,
            reason: getNotDisseminatedReason(notDisseminated),
            advisories,
            cogId,
            verificationUrl,
          };
        }
        return {
          ...base,
          outcome,
          capIdentifier,
          advisoryCode: primaryAdvisory?.code ?? '',
          advisoryMessage: primaryAdvisory?.backendMessage ?? 'FEMA returned an advisory.',
          advisories,
          cogId,
          verificationUrl,
        };
      case 'warning':
        return {
          ...base,
          outcome,
          capIdentifier,
          warningCode: primaryAdvisory?.code,
          warningMessage: primaryAdvisory?.backendMessage ?? 'FEMA returned a warning.',
          advisories,
          cogId,
          verificationUrl,
        };
      case 'rejection':
        return {
          ...base,
          outcome,
          rejectionCode: primaryAdvisory?.code,
          rejectionReason: primaryAdvisory?.backendMessage ?? 'FEMA rejected the message.',
          advisories,
        };
      case 'network-failure':
        return {
          ...base,
          outcome,
          errorMessage: primaryAdvisory?.backendMessage ?? 'The connection to FEMA failed.',
          retryable: true,
        };
      default:
        return {
          ...base,
          outcome: 'transport-unknown',
          transportMessage: `The message reached ${body.resultClass.toLowerCase().replace('_', ' ')}.`,
        };
    }
  }

  return {
    ...base,
    outcome: 'transport-unknown',
    transportMessage: 'The backend answered in a shape this version does not recognize.',
  };
};

/**
 * Process a FEMA submission outcome into a simple acceptance verdict.
 * @param outcome IPAWS outcome returned by the backend submit route.
 * @returns Acceptance flag and operator-facing message.
 */
export const processFemaSubmissionOutcome = (
  outcome: FemaSubmissionOutcome,
): FemaSubmissionProcessedResult => {
  if (outcome.outcome === 'acceptance') {
    return {
      valid: true,
      acceptanceMessage: 'Accepted.',
      message: `CAP ID: ${outcome.capIdentifier}. Accepted at: ${outcome.acceptedAt}.`,
    };
  }

  if (outcome.outcome === 'advisory') {
    return {
      valid: true,
      acceptanceMessage: 'Accepted with a note.',
      message: `Advisory ${outcome.advisoryCode}: ${outcome.advisoryMessage}`,
    };
  }

  if (outcome.outcome === 'warning') {
    const warningCode = outcome.warningCode ? `${outcome.warningCode}: ` : '';

    return {
      valid: true,
      acceptanceMessage: 'Accepted with a concern.',
      message: `Warning ${warningCode}${outcome.warningMessage}`,
    };
  }

  if (outcome.outcome === 'accepted-not-disseminated') {
    return {
      valid: true,
      acceptanceMessage: 'Accepted, but NOT disseminated.',
      message: `CAP ID: ${outcome.capIdentifier}. ${outcome.reason}`,
    };
  }

  if (outcome.outcome === 'rejection') {
    return {
      valid: false,
      acceptanceMessage: 'Not sent.',
      message: `Rejected: ${outcome.rejectionReason}.`,
      findings: outcome.findings,
    };
  }

  if (outcome.outcome === 'transport-unknown') {
    return {
      valid: false,
      acceptanceMessage: 'Not confirmed.',
      message: `${outcome.transportMessage} Missing: explicit IPAWS acceptance response with CAP identifier.`,
    };
  }

  return {
    valid: false,
    acceptanceMessage: 'Not sent.',
    message: `Network or connection failure: ${outcome.errorMessage} Missing: successful retry and IPAWS acceptance response.`,
  };
};
